import * as Sentry from '@sentry/react';

import { NEW_USER_QUIZ_BYPASS_STORAGE_KEY, NEW_USER_QUIZ_BYPASS_TTL_MS } from './constants';

const captureStorageError = (operation: 'read' | 'write', error: unknown) => {
  Sentry.captureException(error, { tags: { new_user_quiz_bypass_storage: operation } });
};

type NewUserQuizBypass = {
  bypassedAt: number;
  userId: string;
};

export const getNewUserQuizBypassStorageKey = (userId: string) =>
  `${NEW_USER_QUIZ_BYPASS_STORAGE_KEY}.${userId}`;

export const getActiveNewUserQuizBypassExpiresAt = (userId: string, now = Date.now()): null | number => {
  try {
    const stored = window.localStorage.getItem(getNewUserQuizBypassStorageKey(userId));
    const bypass = stored === null ? null : (JSON.parse(stored) as Partial<NewUserQuizBypass>);
    const elapsed = typeof bypass?.bypassedAt === 'number' ? now - bypass.bypassedAt : Number.NaN;

    const isActive =
      bypass?.userId === userId &&
      Number.isFinite(elapsed) &&
      elapsed >= 0 &&
      elapsed < NEW_USER_QUIZ_BYPASS_TTL_MS;

    return isActive && typeof bypass.bypassedAt === 'number'
      ? bypass.bypassedAt + NEW_USER_QUIZ_BYPASS_TTL_MS
      : null;
  } catch (error) {
    captureStorageError('read', error);
    return null;
  }
};

export const hasActiveNewUserQuizBypass = (userId: string): boolean =>
  getActiveNewUserQuizBypassExpiresAt(userId) !== null;

export const markNewUserQuizBypassed = (userId: string): void => {
  try {
    window.localStorage.setItem(
      getNewUserQuizBypassStorageKey(userId),
      JSON.stringify({ bypassedAt: Date.now(), userId } satisfies NewUserQuizBypass),
    );
  } catch (error) {
    captureStorageError('write', error);
  }
};
